export type ReaderFontStatus = 'active' | 'inactive'

const TYPEKIT_ID = 'ttz8grx'
const TYPEKIT_SCRIPT_TIMEOUT_MS = 3000
export const READER_FONT_READY_TIMEOUT_MS = 8000
export const READER_FONT_FAMILIES = [
  'source-han-serif-japanese',
  'hiragino-mincho-pron',
  'hiragino-kaku-gothic-pron'
] as const
/**
 * Families served from our own origin as static unicode-range slices
 * (public/fonts/…) instead of Adobe's dynamically subset kit. Static URLs
 * mean the browser's HTTP cache shares the slices across every section
 * iframe and across sessions, and coverage is the full font — the kit's
 * per-document subsets topped out at ~85–89% of a book's glyphs (measured),
 * with everything else falling back to an OS face.
 */
const SELF_HOSTED_FAMILIES = new Set<string>([
  'source-han-serif-japanese',
  // Since 2026-08-29 the Hiragino choices resolve through local system fonts
  // plus the self-hosted nets (see contentFixes' tails) instead of Typekit's
  // dynamically subset kit — the family names themselves have no faces, which
  // is harmless: resolution falls straight through to the tail.
  'hiragino-mincho-pron',
  'hiragino-kaku-gothic-pron'
])

const TYPEKIT_FONT_FAMILIES = new Set<string>(
  READER_FONT_FAMILIES.filter(family => !SELF_HOSTED_FAMILIES.has(family))
)
const READER_FONT_VARIANTS: Record<
  string,
  readonly { variation: string; weight: string }[]
> = {
  'source-han-serif-japanese': [{ variation: 'n6', weight: '600' }],
  'hiragino-mincho-pron': [
    { variation: 'n3', weight: '300' },
    { variation: 'n6', weight: '600' }
  ],
  'hiragino-kaku-gothic-pron': [
    { variation: 'n3', weight: '300' },
    { variation: 'n6', weight: '600' }
  ]
}
const DEFAULT_READER_FONT_SAMPLE =
  '日本語の表示確認漢字ひらがなカタカナ文章天地人々ABCabc123'
const MAX_FONT_SAMPLE_CHARACTERS = 4096

interface TypekitConfig {
  kitId: string
  scriptTimeout: number
  async: boolean
  active: () => void
  inactive: () => void
  fontactive: (fontFamily: string, variation: string) => void
  fontinactive: (fontFamily: string, variation: string) => void
}

interface TypekitWindow extends Window {
  Typekit?: {
    load: (config: TypekitConfig) => void
  }
}

interface TypekitArtifacts {
  headNodes: Set<Node>
  fontFaces: Set<FontFace>
  frameElement?: Element | null
  frozen: boolean
  observer?: MutationObserver
  cleanupTimer?: number
}

interface EnsureReaderFontOptions {
  /** Keep a timed-out iframe on fallback by blocking late Typekit font swaps. */
  freezeOnInactive?: boolean
}

type TypekitLoadingStatus = 'idle' | 'loading' | ReaderFontStatus

interface TypekitDocumentState {
  status: TypekitLoadingStatus
  activeVariations: Map<string, Set<string>>
  inactiveVariations: Map<string, Set<string>>
  listeners: Set<() => void>
  startPromise?: Promise<boolean>
}

const typekitScriptPromises = new WeakMap<Document, Promise<boolean>>()
const typekitStates = new WeakMap<Document, TypekitDocumentState>()
const fontPromises = new WeakMap<
  Document,
  Map<string, Promise<ReaderFontStatus>>
>()
const typekitArtifacts = new WeakMap<Document, TypekitArtifacts>()

/** Quick in-memory verification budget for adopted faces; generous, never hit in practice. */
const ADOPTED_FONT_VERIFY_TIMEOUT_MS = 1500

interface CapturedFaceSource {
  family: string
  source: ArrayBuffer | string
  descriptors?: FontFaceDescriptors
}

/**
 * Sources of every reader FontFace the Typekit kit constructed once this
 * session, keyed by normalized family. A fresh section iframe rebuilds these
 * with its own realm's `new FontFace(...)` instead of borrowing the live face
 * object from another document: adding a foreign-realm FontFace to a
 * FontFaceSet is outside what the CSS Font Loading spec guarantees, so only a
 * realm-local rebuild is safe across engines. The captured bytes cost ~0
 * network and render identically to the original bootstrap.
 */
const capturedFaceSources = new Map<string, CapturedFaceSource[]>()

/**
 * Families whose captured set already rebuilt and verified successfully once.
 * Later bootstraps (each hidden iframe runs its own) would append duplicate
 * records — every reader font is ~1MB, so stop capturing once a family's set
 * is known to be complete.
 */
const completeFaceFamilies = new Set<string>()

/**
 * True while adoptCachedFamilyFaces is constructing faces. The capture hook
 * must ignore those constructions: on a window whose FontFace is already
 * hooked (the main document after its own bootstrap), capturing a face that
 * was itself built from the cache appends to the very array the adopt loop is
 * iterating — the loop then never terminates and the page freezes.
 */
let adoptingFromCache = false

/**
 * Wrap `FontFace` in `targetWindow` before the Typekit kit runs there, so
 * every face the kit constructs for a reader family leaves us a copy of its
 * bytes. The wrapper preserves the prototype; the kit keeps working unchanged.
 */
const captureFontFaceSources = (targetWindow: Window) => {
  const anyWindow = targetWindow as Window & {
    FontFace: typeof FontFace
    __genkenFontFaceHooked?: boolean
  }
  if (anyWindow.__genkenFontFaceHooked || !anyWindow.FontFace) return
  anyWindow.__genkenFontFaceHooked = true

  const OriginalFontFace = anyWindow.FontFace
  const Hooked = function (
    family: string,
    source: ArrayBuffer | ArrayBufferView | string,
    descriptors?: FontFaceDescriptors
  ) {
    const face = new OriginalFontFace(family, source as never, descriptors)
    try {
      const normalized = normalizeFontFamily(family)
      if (
        !adoptingFromCache &&
        TYPEKIT_FONT_FAMILIES.has(normalized) &&
        !completeFaceFamilies.has(normalized)
      ) {
        let copy: ArrayBuffer | string | null = null
        if (typeof source === 'string') copy = source
        else if (source instanceof ArrayBuffer) copy = source.slice(0)
        else if (ArrayBuffer.isView(source)) {
          copy = source.buffer.slice(
            source.byteOffset,
            source.byteOffset + source.byteLength
          )
        }
        if (copy !== null) {
          const records = capturedFaceSources.get(normalized) ?? []
          records.push({ family, source: copy, descriptors })
          capturedFaceSources.set(normalized, records)
        }
      }
    } catch {
      // Capturing is best-effort; the face itself must always be returned.
    }
    return face
  }
  Hooked.prototype = OriginalFontFace.prototype
  anyWindow.FontFace = Hooked as unknown as typeof FontFace
}

/**
 * Rebuild the family's faces inside `targetDocument`'s own realm from the
 * captured sources, then verify one actually renders. Returns 'active' on
 * success; null sends the caller down the regular bootstrap path (and a
 * failed verification clears the cache, so a bad entry heals itself).
 */
const adoptCachedFamilyFaces = async (
  targetDocument: Document,
  fontFamily: string
): Promise<ReaderFontStatus | null> => {
  const normalized = normalizeFontFamily(fontFamily)
  const records = capturedFaceSources.get(normalized)
  if (!records?.length) return null

  const targetWindow = targetDocument.defaultView as
    | (Window & { FontFace: typeof FontFace })
    | null
  if (!targetWindow?.FontFace || !targetDocument.fonts?.add) return null

  const built: FontFace[] = []
  adoptingFromCache = true
  try {
    // Iterate a snapshot: the live array must never be the loop's subject
    // while faces are being constructed (see adoptingFromCache above).
    for (const record of [...records]) {
      const face = new targetWindow.FontFace(
        record.family,
        record.source as never,
        record.descriptors
      )
      targetDocument.fonts.add(face)
      built.push(face)
    }
  } catch {
    built.forEach(face => {
      try {
        targetDocument.fonts.delete(face)
      } catch {
        // Never added — nothing to undo.
      }
    })
    capturedFaceSources.delete(normalized)
    return null
  } finally {
    adoptingFromCache = false
  }

  const weight = records[0].descriptors?.weight ?? '400'
  const sampleText = createFontSample(targetDocument)
  const verified = await Promise.race([
    targetDocument.fonts
      .load(`${weight} 1em "${fontFamily}"`, sampleText)
      .then(loaded => loaded.length > 0)
      .catch(() => false),
    new Promise<boolean>(resolve =>
      targetWindow.setTimeout(
        () => resolve(false),
        ADOPTED_FONT_VERIFY_TIMEOUT_MS
      )
    )
  ])
  if (!verified) {
    built.forEach(face => {
      try {
        targetDocument.fonts.delete(face)
      } catch {
        // Already gone.
      }
    })
    capturedFaceSources.delete(normalized)
    completeFaceFamilies.delete(normalized)
    return null
  }
  completeFaceFamilies.add(normalized)
  return 'active'
}

const normalizeFontFamily = (fontFamily: string) =>
  fontFamily.trim().replace(/^["']|["']$/g, '').toLowerCase()

const isReaderTypekitFontFace = (fontFace: FontFace) =>
  TYPEKIT_FONT_FAMILIES.has(normalizeFontFamily(fontFace.family))

const getCurrentFontFaces = (targetDocument: Document) => {
  const fontSet = targetDocument.fonts
  if (!fontSet || !(Symbol.iterator in Object(fontSet))) {
    return new Set<FontFace>()
  }

  return new Set(Array.from(fontSet))
}

const getTypekitArtifacts = (targetDocument: Document) => {
  const cached = typekitArtifacts.get(targetDocument)
  if (cached) return cached

  const artifacts: TypekitArtifacts = {
    headNodes: new Set(Array.from(targetDocument.head.childNodes)),
    fontFaces: getCurrentFontFaces(targetDocument),
    frameElement: targetDocument.defaultView?.frameElement,
    frozen: false
  }
  typekitArtifacts.set(targetDocument, artifacts)
  return artifacts
}

const updateLoadingClass = (
  targetDocument: Document,
  status: ReaderFontStatus
) => {
  const root = targetDocument.documentElement
  root.classList.remove('wf-loading', 'wf-active', 'wf-inactive')
  root.classList.add(status === 'active' ? 'wf-active' : 'wf-inactive')
}

const isTypekitHeadNode = (node: Node) => {
  if (node.nodeType !== 1) return false
  const element = node as Element

  if (
    element.matches('script[data-genken-typekit]') ||
    element.matches('script[src*="typekit.net"], link[href*="typekit.net"]')
  ) {
    return true
  }

  if (element.tagName !== 'STYLE') return false
  const styleElement = element as HTMLStyleElement

  let cssText = styleElement.textContent ?? ''
  try {
    cssText += Array.from(styleElement.sheet?.cssRules ?? [])
      .map(rule => rule.cssText)
      .join('')
  } catch {
    // A cross-origin sheet cannot be inspected. Typekit link elements are
    // already identified by href above.
  }

  return /source-han-serif-japanese|hiragino-(?:mincho|kaku)-pron|_fff_|\.tk-|\.wf-/i.test(
    cssText
  )
}

const removeLateTypekitArtifacts = (targetDocument: Document) => {
  const artifacts = typekitArtifacts.get(targetDocument)
  if (!artifacts?.frozen) return

  Array.from(targetDocument.head.childNodes).forEach(node => {
    if (!artifacts.headNodes.has(node) && isTypekitHeadNode(node)) {
      node.parentNode?.removeChild(node)
    }
  })

  getCurrentFontFaces(targetDocument).forEach(fontFace => {
    if (
      !artifacts.fontFaces.has(fontFace) &&
      isReaderTypekitFontFace(fontFace)
    ) {
      targetDocument.fonts.delete(fontFace)
    }
  })
}

const freezeTypekitFallback = (targetDocument: Document) => {
  const artifacts = getTypekitArtifacts(targetDocument)
  if (artifacts.frameElement && !artifacts.frameElement.isConnected) return
  if (artifacts.frozen) return
  artifacts.frozen = true

  const fontSet = targetDocument.fonts
  if (fontSet?.add) {
    const originalAdd = fontSet.add.bind(fontSet)
    try {
      Object.defineProperty(fontSet, 'add', {
        configurable: true,
        value: (fontFace: FontFace) => {
          if (
            artifacts.frozen &&
            !artifacts.fontFaces.has(fontFace) &&
            isReaderTypekitFontFace(fontFace)
          ) {
            return fontSet
          }
          return originalAdd(fontFace)
        }
      })
    } catch {
      // Some engines do not allow overriding FontFaceSet.add. The cleanup
      // observer/timer below still removes late registrations before reflow.
    }
  }

  removeLateTypekitArtifacts(targetDocument)
  artifacts.observer = new MutationObserver(() =>
    removeLateTypekitArtifacts(targetDocument)
  )
  artifacts.observer.observe(targetDocument.head, {
    childList: true,
    subtree: true
  })

  const targetWindow = targetDocument.defaultView
  if (targetWindow) {
    artifacts.cleanupTimer = targetWindow.setInterval(
      () => removeLateTypekitArtifacts(targetDocument),
      50
    )
    targetWindow.addEventListener(
      'pagehide',
      () => {
        artifacts.observer?.disconnect()
        if (artifacts.cleanupTimer !== undefined) {
          targetWindow.clearInterval(artifacts.cleanupTimer)
        }
      },
      { once: true }
    )
  }
}

const loadTypekitScript = (targetDocument: Document) => {
  const targetWindow = targetDocument.defaultView as TypekitWindow | null
  if (targetWindow?.Typekit) return Promise.resolve(true)

  const cached = typekitScriptPromises.get(targetDocument)
  if (cached) return cached

  getTypekitArtifacts(targetDocument)
  const promise = new Promise<boolean>(resolve => {
    let settled = false
    const finish = (loaded: boolean) => {
      if (settled) return
      settled = true
      window.clearTimeout(timeoutId)
      resolve(loaded)
    }
    const timeoutId = window.setTimeout(
      () => finish(false),
      TYPEKIT_SCRIPT_TIMEOUT_MS
    )

    const script = targetDocument.createElement('script')
    script.dataset.genkenTypekit = TYPEKIT_ID
    script.src = `https://use.typekit.net/${TYPEKIT_ID}.js`
    script.async = true
    script.onload = () => finish(Boolean(targetWindow?.Typekit))
    script.onerror = () => finish(false)
    targetDocument.head.appendChild(script)
  })

  typekitScriptPromises.set(targetDocument, promise)
  void promise.then(loaded => {
    if (!loaded && typekitScriptPromises.get(targetDocument) === promise) {
      typekitScriptPromises.delete(targetDocument)
    }
  })
  return promise
}

const getTypekitState = (targetDocument: Document) => {
  const cached = typekitStates.get(targetDocument)
  if (cached) return cached

  const state: TypekitDocumentState = {
    status: 'idle',
    activeVariations: new Map(),
    inactiveVariations: new Map(),
    listeners: new Set()
  }
  typekitStates.set(targetDocument, state)
  return state
}

const notifyTypekitListeners = (state: TypekitDocumentState) => {
  state.listeners.forEach(listener => listener())
}

const recordTypekitVariation = (
  variations: Map<string, Set<string>>,
  fontFamily: string,
  variation: string
) => {
  const normalizedFontFamily = normalizeFontFamily(fontFamily)
  let familyVariations = variations.get(normalizedFontFamily)
  if (!familyVariations) {
    familyVariations = new Set()
    variations.set(normalizedFontFamily, familyVariations)
  }
  familyVariations.add(variation.toLowerCase())
}

/**
 * Start Adobe Fonts once for this document. Resolving this promise only means
 * the kit started; selected-family readiness is tracked separately below.
 */
const startTypekit = (targetDocument: Document) => {
  const state = getTypekitState(targetDocument)
  if (state.startPromise && state.status !== 'inactive') {
    return state.startPromise
  }
  state.startPromise = undefined

  const targetWindow = targetDocument.defaultView as TypekitWindow | null
  // Before the kit runs here, so every reader face it builds leaves its bytes.
  if (targetWindow) captureFontFaceSources(targetWindow as Window)
  state.status = 'loading'
  targetDocument.documentElement.classList.add('wf-loading')

  const promise = loadTypekitScript(targetDocument).then(loaded => {
    if (!loaded || !targetWindow?.Typekit) {
      state.status = 'inactive'
      notifyTypekitListeners(state)
      return false
    }

    try {
      targetWindow.Typekit.load({
        kitId: TYPEKIT_ID,
        scriptTimeout: READER_FONT_READY_TIMEOUT_MS,
        async: true,
        fontactive: (fontFamily, variation) => {
          recordTypekitVariation(
            state.activeVariations,
            fontFamily,
            variation
          )
          notifyTypekitListeners(state)
        },
        fontinactive: (fontFamily, variation) => {
          recordTypekitVariation(
            state.inactiveVariations,
            fontFamily,
            variation
          )
          notifyTypekitListeners(state)
        },
        active: () => {
          state.status = 'active'
          notifyTypekitListeners(state)
        },
        inactive: () => {
          state.status = 'inactive'
          notifyTypekitListeners(state)
        }
      })
      return true
    } catch {
      state.status = 'inactive'
      notifyTypekitListeners(state)
      return false
    }
  })

  state.startPromise = promise
  void promise.then(started => {
    // A bootstrap that arrived just after its timeout may be retried by the
    // parent document. A frozen iframe never calls ensureReaderFont again.
    if (!started && state.startPromise === promise) {
      state.startPromise = undefined
    }
  })
  return promise
}

const getFamilyReadiness = (
  state: TypekitDocumentState,
  normalizedFontFamily: string
): ReaderFontStatus | undefined => {
  const expectedVariations = READER_FONT_VARIANTS[normalizedFontFamily].map(
    variant => variant.variation
  )
  const activeVariations = state.activeVariations.get(normalizedFontFamily)
  if (
    expectedVariations.every(variation => activeVariations?.has(variation))
  ) {
    return 'active'
  }

  const inactiveVariations = state.inactiveVariations.get(normalizedFontFamily)
  if (
    expectedVariations.some(variation => inactiveVariations?.has(variation))
  ) {
    return 'inactive'
  }
  if (state.status === 'inactive') return 'inactive'

  // Some Typekit versions omit family callbacks. Global active still means
  // the @font-face rules are ready, so FontFaceSet.load can verify the family.
  if (state.status === 'active') return 'active'
  return undefined
}

const waitForTypekitFamily = (
  targetDocument: Document,
  fontFamily: string,
  timeoutMs: number
) => {
  const state = getTypekitState(targetDocument)
  const normalizedFontFamily = normalizeFontFamily(fontFamily)
  const initialStatus = getFamilyReadiness(state, normalizedFontFamily)
  if (initialStatus) return Promise.resolve(initialStatus)

  return new Promise<ReaderFontStatus>(resolve => {
    let settled = false
    const finish = (status: ReaderFontStatus) => {
      if (settled) return
      settled = true
      window.clearTimeout(timeoutId)
      state.listeners.delete(checkReadiness)
      resolve(status)
    }
    const checkReadiness = () => {
      const status = getFamilyReadiness(state, normalizedFontFamily)
      if (status) finish(status)
    }
    const timeoutId = window.setTimeout(
      () => finish('inactive'),
      timeoutMs
    )

    state.listeners.add(checkReadiness)
    checkReadiness()
  })
}

const createFontSample = (targetDocument: Document) => {
  const documentText = targetDocument.body?.textContent ?? ''
  const uniqueCharacters = Array.from(
    new Set(
      Array.from(`${DEFAULT_READER_FONT_SAMPLE}${documentText}`.replace(/\s/g, ''))
    )
  )
    .slice(0, MAX_FONT_SAMPLE_CHARACTERS)
    .join('')

  return uniqueCharacters || DEFAULT_READER_FONT_SAMPLE
}

const createFontProbe = (
  targetDocument: Document,
  fontFamily: string,
  sampleText: string
) => {
  if (!targetDocument.body) return undefined

  const probe = targetDocument.createElement('span')
  probe.dataset.genkenFontProbe = fontFamily
  probe.textContent = sampleText
  Object.assign(probe.style, {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    visibility: 'hidden',
    pointerEvents: 'none',
    fontFamily: `"${fontFamily}"`,
    fontSize: '16px'
  })
  targetDocument.body.appendChild(probe)
  return probe
}

const waitForDocumentFont = (
  targetDocument: Document,
  fontFamily: string,
  sampleText: string,
  timeoutMs: number
) =>
  new Promise<ReaderFontStatus>(resolve => {
    let settled = false
    const finish = (status: ReaderFontStatus) => {
      if (settled) return
      settled = true
      window.clearTimeout(timeoutId)
      resolve(status)
    }
    const timeoutId = window.setTimeout(
      () => finish('inactive'),
      timeoutMs
    )

    const variants = READER_FONT_VARIANTS[normalizeFontFamily(fontFamily)]
    void Promise.all(
      variants.map(variant =>
        targetDocument.fonts.load(
          `${variant.weight} 1em "${fontFamily}"`,
          sampleText
        )
      )
    )
      .then(fontFaceGroups =>
        finish(
          fontFaceGroups.every(fontFaces => fontFaces.length > 0)
            ? 'active'
            : 'inactive'
        )
      )
      .catch(() => finish('inactive'))
  })

const loadDocumentFont = async (
  targetDocument: Document,
  fontFamily: string
): Promise<ReaderFontStatus> => {
  if (!targetDocument.fonts) return 'inactive'

  // A document whose own bootstrap already activated this kit needs nothing:
  // its faces are live. Re-adopting here is at best redundant work on the
  // main document every time the settings panel opens.
  if (typekitStates.get(targetDocument)?.status === 'active') return 'active'

  // Faces this session already downloaded skip the whole bootstrap.
  const adopted = await adoptCachedFamilyFaces(targetDocument, fontFamily)
  if (adopted) return adopted

  const sampleText = createFontSample(targetDocument)
  const probe = createFontProbe(targetDocument, fontFamily, sampleText)

  try {
    const started = await startTypekit(targetDocument)
    if (!started) return 'inactive'

    const readinessStartedAt = Date.now()
    const familyStatus = await waitForTypekitFamily(
      targetDocument,
      fontFamily,
      READER_FONT_READY_TIMEOUT_MS
    )
    if (familyStatus !== 'active') return 'inactive'

    const remainingTimeout = Math.max(
      0,
      READER_FONT_READY_TIMEOUT_MS - (Date.now() - readinessStartedAt)
    )
    if (remainingTimeout === 0) return 'inactive'

    const status = await waitForDocumentFont(
      targetDocument,
      fontFamily,
      sampleText,
      remainingTimeout
    )
    return status
  } finally {
    probe?.remove()
  }
}

/** Preload only the small Typekit bootstrap; do not start all font downloads. */
export const preloadTypekit = (targetDocument: Document = document) =>
  loadTypekitScript(targetDocument)

/**
 * Make the selected reader font available in a specific document. Fonts are
 * document-scoped, while the downloaded font bytes are reused from HTTP cache.
 */
// Versioned: /static/ is immutable-cached for a year, so content changes
// must arrive under a new name (see the note inside the css file).
const SELF_HOSTED_CSS_PATH = '/static/fonts/source-han-serif-japanese.v3.css'
const SELF_HOSTED_SANS_CSS_PATH = '/static/fonts/genken-sans-jp.v1.css'
const SELF_HOSTED_LINK_ATTR = 'data-genken-self-hosted-font'

/**
 * Make a self-hosted family usable inside `targetDocument`: link its
 * @font-face slices, then start fetching the slices this document's own text
 * needs. Resolution is not gated on the fetches — `font-display: swap` shows
 * the same-style local fallback immediately and the arriving glyphs are all
 * one em wide, so the swap moves nothing (measured: pagination identical
 * before/after) — but kicking the loads off here means the swap window is
 * the network round-trip only.
 */
const ensureSelfHostedFont = (
  targetDocument: Document,
  fontFamily: string
): Promise<ReaderFontStatus> => {
  try {
    if (
      targetDocument.head &&
      !targetDocument.querySelector(`link[${SELF_HOSTED_LINK_ATTR}]`)
    ) {
      for (const path of [SELF_HOSTED_CSS_PATH, SELF_HOSTED_SANS_CSS_PATH]) {
        const link = targetDocument.createElement('link')
        link.rel = 'stylesheet'
        link.href = `${window.location.origin}${path}`
        link.setAttribute(SELF_HOSTED_LINK_ATTR, 'true')
        targetDocument.head.appendChild(link)
      }
    }
    const text = targetDocument.body?.textContent ?? ''
    if (text && targetDocument.fonts?.load) {
      // Kick off slice fetches for whichever self-hosted net this choice can
      // reach; on devices whose local fonts answer first these load nothing.
      const netFamily = /gothic|kaku/i.test(fontFamily)
        ? 'genken-sans-jp'
        : 'source-han-serif-japanese'
      const sample = Array.from(new Set(text)).join('').slice(0, 2000)
      void targetDocument.fonts.load(`400 1em "${netFamily}"`, sample)
      void targetDocument.fonts.load(`600 1em "${netFamily}"`, sample)
    }
  } catch {
    // The CSS fallback tail keeps the page readable either way.
  }
  targetDocument.documentElement.dataset.genkenFontStatus = 'active'
  return Promise.resolve('active')
}

export const ensureReaderFont = (
  targetDocument: Document,
  fontFamily: string,
  options: EnsureReaderFontOptions = {}
): Promise<ReaderFontStatus> => {
  if (SELF_HOSTED_FAMILIES.has(fontFamily)) {
    return ensureSelfHostedFont(targetDocument, fontFamily)
  }
  if (!TYPEKIT_FONT_FAMILIES.has(fontFamily)) {
    targetDocument.documentElement.dataset.genkenFontStatus = 'active'
    return Promise.resolve('active')
  }

  let documentFonts = fontPromises.get(targetDocument)
  if (!documentFonts) {
    documentFonts = new Map()
    fontPromises.set(targetDocument, documentFonts)
  }

  let promise = documentFonts.get(fontFamily)
  if (!promise) {
    promise = loadDocumentFont(targetDocument, fontFamily)
    documentFonts.set(fontFamily, promise)
  }

  return promise.then(status => {
    targetDocument.documentElement.dataset.genkenFontStatus = status
    updateLoadingClass(targetDocument, status)
    if (status === 'inactive' && options.freezeOnInactive) {
      freezeTypekitFallback(targetDocument)
    } else if (
      status === 'inactive' &&
      documentFonts?.get(fontFamily) === promise
    ) {
      // The parent document may retry if the bootstrap arrived just after its
      // timeout. A frozen iframe must stay on the layout it already measured.
      documentFonts?.delete(fontFamily)
    }
    return status
  })
}

/** Warm all reader choices when the settings panel is opened. */
export const warmReaderFonts = (
  targetDocument: Document = document,
  fontFamilies: readonly string[] = READER_FONT_FAMILIES
) =>
  Promise.all(
    fontFamilies.map(fontFamily =>
      ensureReaderFont(targetDocument, fontFamily)
    )
  )
